import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

/**
 * Homepage · Section 2 — Decision Dashboard
 *
 * Editorial grid of eight questions a serious buyer actually asks before
 * they commit six figures to a neighbourhood. Bloomberg-terminal restraint
 * meets NYT typography. Sharp corners, hairline rules, no gradients,
 * no oversized cards. Each card is a full-bleed hover surface with a subtle
 * reveal of the corresponding data lever underneath the question.
 */

const CARDS = [
    {
        n: '01',
        q: 'Will it appreciate?',
        a: 'Five-year forecast, zoning velocity, and GO Rail expansion.',
        tag: 'Forecast',
        to: '/invest',
    },
    {
        n: '02',
        q: 'Are the schools any good?',
        a: 'EQAO scores and catchment maps — from board data, not listing copy.',
        tag: 'Schools',
        to: '/neighbourhoods?tag=schools',
    },
    {
        n: '03',
        q: 'What\u2019s the commute really like?',
        a: 'Peak-hour transit and drive times — modelled, not estimated.',
        tag: 'Commute',
        to: '/neighbourhoods?tag=commuter',
    },
    {
        n: '04',
        q: 'Who lives here?',
        a: 'Age mix, income bands, and household composition — the street\u2019s real portrait.',
        tag: 'Demographics',
        to: '/quiz',
    },
    {
        n: '05',
        q: 'Am I paying a fair price?',
        a: 'Price per square foot benchmarked to street and block — plus what a bank will carry.',
        tag: 'Value',
        to: '/affordability',
    },
];

const container = {
    hidden: {},
    show: {
        transition: { staggerChildren: 0.06, delayChildren: 0.05 },
    },
};

const item = {
    hidden: { opacity: 0, y: 14 },
    show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 0.61, 0.36, 1] } },
};

export default function DecisionDashboard() {
    return (
        <section
            data-testid="decision-dashboard"
            className="border-b border-neutral-200 bg-[#FAFAF8]"
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-20 lg:pb-28">
                {/* Header — editorial anchor */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.7, ease: [0.22, 0.61, 0.36, 1] }}
                    className="max-w-4xl"
                >
                    <div className="flex items-center gap-3 text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data">
                        <span className="inline-block w-6 h-px bg-neutral-400" />
                        Section 02 · The Decision Dashboard
                    </div>
                    <h2
                        className="font-editorial tracking-editorial mt-6 leading-[1.03] text-black"
                        style={{ fontSize: 'clamp(36px, 4.8vw, 68px)', letterSpacing: '-0.02em' }}
                        data-testid="decision-dashboard-title"
                    >
                        Before You Move…
                        <br />
                        <span className="italic text-neutral-500">Ask Better Questions.</span>
                    </h2>
                    <p className="mt-6 font-editorial text-neutral-600 max-w-2xl" style={{ fontSize: 'clamp(16px, 1.15vw, 19px)', lineHeight: 1.55 }}>
                        That&apos;s exactly what BestNeighbourhoods.ca helps you answer — five questions,
                        one honest score, and the data behind every one.
                    </p>
                </motion.div>

                {/* Grid — 5 across on desktop, 2 on tablet, 1 on mobile */}
                <motion.div
                    variants={container}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: '-60px' }}
                    className="mt-14 lg:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 border-l border-t border-neutral-200"
                    data-testid="decision-dashboard-grid"
                >
                    {CARDS.map((c) => (
                        <motion.div
                            key={c.n}
                            variants={item}
                            data-testid={`decision-card-${c.n}`}
                        >
                            <Link
                                to={c.to}
                                className="group relative block border-r border-b border-neutral-200 bg-white hover:bg-brand-navy transition-colors duration-500 h-full"
                                data-testid={`decision-card-link-${c.n}`}
                            >
                                <div className="p-7 lg:p-8 min-h-[260px] lg:min-h-[300px] flex flex-col">
                                    {/* Number + tag row */}
                                    <div className="flex items-center justify-between">
                                        <div className="font-mono-data text-[12px] tabular text-neutral-400 group-hover:text-brand-gold transition-colors duration-500">
                                            {c.n}
                                        </div>
                                        <div className="font-mono-data text-[10px] tracking-[0.22em] uppercase text-neutral-400 group-hover:text-white/60 transition-colors duration-500">
                                            {c.tag}
                                        </div>
                                    </div>

                                    {/* Question */}
                                    <h3
                                        className="font-editorial tracking-editorial mt-8 leading-[1.18] text-brand-navy group-hover:text-white transition-colors duration-500"
                                        style={{ fontSize: '22px', letterSpacing: '-0.01em' }}
                                    >
                                        {c.q}
                                    </h3>

                                    {/* Answer */}
                                    <div className="mt-auto pt-6">
                                        <p className="text-[13.5px] leading-relaxed text-neutral-600 group-hover:text-white/80 transition-colors duration-500">
                                            {c.a}
                                        </p>
                                    </div>

                                    {/* Arrow reveal on hover — bottom-right */}
                                    <div className="absolute bottom-6 right-6 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500 ease-out">
                                        <ArrowUpRight className="w-4 h-4 text-brand-gold" />
                                    </div>

                                    {/* Hairline accent — animates in from left on hover */}
                                    <div className="absolute bottom-0 left-0 h-px bg-brand-gold/70 w-0 group-hover:w-full transition-[width] duration-700 ease-out" />
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </motion.div>

                {/* Footer editorial CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 0.61, 0.36, 1] }}
                    className="mt-14 lg:mt-20 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 pt-10 border-t border-neutral-200"
                >
                    <div className="max-w-xl">
                        <div className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 font-mono-data">
                            Independent · Verified · Free to explore
                        </div>
                        <p className="mt-4 font-editorial italic text-neutral-700 leading-snug" style={{ fontSize: '20px' }}>
                            &ldquo;A neighbourhood is a hundred small decisions. We&apos;ve done the
                            homework on all of them.&rdquo;
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <Link
                            to="/neighbourhoods"
                            data-testid="decision-cta-explore"
                            className="bn-btn-primary"
                        >
                            Explore neighbourhoods
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        <Link
                            to="/strategy-session"
                            data-testid="decision-cta-strategy"
                            className="bn-btn-secondary"
                        >
                            Book a strategy session
                        </Link>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
